import axios from "axios";
import { useNavigate, useParams } from "react-router-dom";
import { useEffect, useMemo, useState } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { useSetAtom } from "jotai";
import pluralize from "pluralize";

import { newToastEvent } from "../../utils/ToastUtil";
import useSharedRecordPage, {
  recordPagePasswordAtom,
} from "./useSharedRecordPage";
import LoadingView from "../SharedViewLayout/LoadingView";
import PasswordProtectedView from "../SharedViewLayout/PasswordProtectedView";
import AppNavbar from "../../components/AppNavbar/AppNavbar";
import { HeaderWrapper } from "../../views/pages/PageView";
import SharedRecordAPI from "../../api/api/SharedRecordAPI";
import { ViewRecordDetails } from "../../components/Drawers/Records/ViewRecordDrawer";
import { GetDefaultLayout } from "../../components/Drawers/Records/ModuleLayoutSelector";
import NotFoundView from "../SharedViewLayout/NotFoundView";
import { LayoutAndSections, Module } from "../../types/TenantInterfaces";
import ThumbnailWidget from "../../components/Widgets/ThumbnailWidget/ThumbnailWidget";
import { setSchemaAtom } from "../../atoms/GlobalAtoms";

export default function SharedRecordPageLayout() {
  const client = useQueryClient();
  const navigate = useNavigate();
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");
  const setRecordPagePassword = useSetAtom(recordPagePasswordAtom);
  const setSchema = useSetAtom(setSchemaAtom);
  const { sharedRecordPageID, tenantID, viewID } = useParams();
  const { sharedRecordPage, sharedRecordPageLoading } = useSharedRecordPage(
    tenantID!,
    sharedRecordPageID!
  );
  const success = sharedRecordPage?.success;
  const requiresPassword = sharedRecordPage?.requiresPassword;
  const [currentRichTextField, setCurrentRichTextField] = useState<any>(null);

  const moduleSchema = useMemo(() => {
    if (!success) return null;

    const view = sharedRecordPage?.subViews.find((v) => v.id === viewID);
    const modules = sharedRecordPage?.schema.CRM.Modules;
    const record = sharedRecordPage?.record;
    const moduleAPIName = view?.ModuleAPIName
      ? view?.ModuleAPIName
      : record._ModuleAPIName;

    return modules?.find((m) => m.APIName === moduleAPIName);
  }, [viewID, sharedRecordPageLoading, success]);

  const [layout, setLayout] = useState<null | LayoutAndSections>(
    moduleSchema && !sharedRecordPageLoading
      ? GetDefaultLayout(moduleSchema, "", sharedRecordPage?.record) || null
      : null
  );

  const currentView = useMemo(() => {
    if (!success) return null;

    const subView = sharedRecordPage?.subViews.find((v) => v.id === viewID);

    if (viewID?.startsWith("RichText")) {
      const fieldAPIName = viewID.split("-", 2)[1];

      const field = moduleSchema?.Fields.find(
        (f) => f.APIName === fieldAPIName
      );
      if (field) {
        setCurrentRichTextField({ ...field, Disabled: true });
      }
    }

    return subView ?? sharedRecordPage?.parentView;
  }, [viewID, sharedRecordPageLoading, success]);

  useEffect(() => {
    if (sharedRecordPageLoading) return;

    setLayout(
      moduleSchema && !sharedRecordPageLoading
        ? GetDefaultLayout(moduleSchema, "", sharedRecordPage?.record) || null
        : null
    );
  }, [sharedRecordPageLoading, moduleSchema, sharedRecordPage?.record]);

  const tabs = useMemo(() => {
    const parentTabs = sharedRecordPage?.parentView?.SubViews;

    if (!parentTabs?.length) return [];

    return parentTabs.map((tab) => {
      let activeValue = tab.id;

      // Rename the Summary tab to "Details" for better UX
      const tabName = tab?.Type === "Summary" ? "Details" : tab?.Name;

      if (tab.Type === "RichText") {
        activeValue = `RichText-${tab?.FieldAPIName}-${activeValue}`;
      }
      if (tab.Type === "Gallery") {
        activeValue = `Gallery-${tab?.FieldAPIName}-${activeValue}`;
      }
      if (tab.Type === "Subitem") {
        activeValue = `Subitem-${tab?.FieldAPIName}-${activeValue}`;
      }

      return {
        title: tabName,
        activeValue,
        externalLink: tab.Type === "External" ? tab.Link : undefined,
      };
    });
  }, [sharedRecordPageLoading, sharedRecordPage?.record]);

  axios.interceptors.response.use(
    (response) => {
      return response;
    },
    (err) => {
      if (
        err?.config?.url?.includes("/getSharedRecordPage/") &&
        err?.response?.status === 400 &&
        err?.response?.data === "Bad Request"
      ) {
        navigate(`/login?r=${window.location.href}`);
        newToastEvent("error", "Please login to access that page.");
      }

      return Promise.reject(err);
    }
  );

  if (sharedRecordPageLoading) return <LoadingView />;

  if (!success && requiresPassword) {
    return (
      <PasswordProtectedView
        onSubmit={updatePassword}
        submitting={submitting}
        error={error}
        title={`Enter Password To View This Record`}
      />
    );
  }

  if (!success) return <NotFoundView />;

  return (
    <div>
      <HeaderWrapper loading={false}>
        <div className="mt-2 mt-sm-3 d-flex justify-content-between align-items-center mb-1">
          <div>
            <RecordHeader
              record={sharedRecordPage?.record}
              module={moduleSchema!}
              loading={sharedRecordPageLoading}
            />
          </div>
        </div>

        {/* @ts-ignore */}
        <AppNavbar
          tabs={tabs}
          activeTab={viewID}
          onSelect={(viewID: string) => {
            navigate(
              `/shared-record-page/${tenantID}/${sharedRecordPageID}/${viewID}`
            );
          }}
          dynamic={false}
        />
      </HeaderWrapper>
      <div className="px-4 pt-4">
        {sharedRecordPageLoading && <LoadingView />}
        {!sharedRecordPageLoading && currentView && (
          <ViewRecordDetails
            record={sharedRecordPage.record}
            recordID={sharedRecordPage.record?.id}
            layout={layout}
            setLayout={setLayout}
            activeTab={viewID ?? ""}
            module={moduleSchema}
            currentRichTextField={currentRichTextField}
            parentViewID={sharedRecordPage?.parentView.id}
            page={{
              Name: sharedRecordPage?.parentView.Name,
              //@ts-ignore
              Views: [currentView],
              Description: "",
              IconURL: "",
              Privacy: "public",
              Owner: "",
              Icon: "",
              Members: [],
              _id: "",
              id: "",
              Created: "",
              CreatedBy: "",
              LastUpdated: "",
              LastUpdatedBy: "",
              Tags: [],
            }}
            // @ts-ignore
            view={currentView}
          />
        )}
      </div>
    </div>
  );

  async function updatePassword(password: string) {
    if (!password) return;

    setError("");
    setSubmitting(true);

    setRecordPagePassword((prev) => ({
      ...prev,
      [`${tenantID}-${sharedRecordPageID}`]: password,
    }));

    try {
      const result = await SharedRecordAPI.GetSharedRecordPage(
        sharedRecordPageID!,
        tenantID!,
        password
      );

      const data = result.data.data;

      if (data.success === false) {
        setError("Invalid password provided.");
      } else if (data.success === true) {
        setSchema(data.schema);

        client.setQueryData(["shared-record-page", sharedRecordPageID], data);

        for (const record of data?.documentRecords || []) {
          client.setQueryData(
            [tenantID, "record", "Documents", record._id],
            record
          );
        }

        for (const flow of data?.flowCharts || []) {
          client.setQueryData([tenantID, "record", "Flows", flow._id], flow);
        }
      }
    } catch {
      setError("An unexpected error");
    } finally {
      setSubmitting(false);
    }
  }

  function RecordHeader({
    record,
    module,
    loading,
  }: {
    record: any;
    module: Module | undefined;
    loading: boolean;
  }) {
    const { imageField, imageFieldPresentation } = useMemo(() => {
      if (!module || !record)
        return { imageField: null, imageFieldPresentation: null };

      if (module?.APIName !== record?._ModuleAPIName)
        return { imageField: null, imageFieldPresentation: null };

      const imageField =
        module?.Templates?.RecordDetailView?.ImageField ?? null;

      if (!imageField)
        return { imageField: null, imageFieldPresentation: null };

      const foundField = module?.Fields.find(
        (field) => field?.APIName === imageField
      );

      return {
        imageField: imageField,
        imageFieldPresentation: foundField?.Presentation ?? null,
      };
    }, [record, module, loading]);

    return (
      <div className="tw:flex tw:justify-between tw:items-center tw:px-2">
        <div>
          <div
            style={{ fontSize: 12 }}
            className="tw:flex tw:items-center text-muted font-weight-500 tw:mb-1"
          >
            {module?.Name && pluralize.singular(module?.Name)}
            {!!record?._TenantUser && (
              <img
                className="ml-1"
                src="/img/GCLogo512x512.png"
                style={{
                  height: 20,
                  width: 20,
                }}
              />
            )}
          </div>
          <div style={{ width: 285 }} className="tw:flex tw:items-center">
            {imageField && (
              <div style={{ position: "relative" }}>
                <ThumbnailWidget
                  record={record}
                  imageKey={imageField}
                  mode={imageFieldPresentation ?? undefined}
                  size="md"
                />
              </div>
            )}
            <div style={{ width: "calc(100% - 20px)" }}>
              <h3
                className="mb-0 mt-0 text-truncate"
                style={{ lineHeight: 1.3 }}
              >
                {record?._RecordName ? record?._RecordName : "(No Name)"}
              </h3>
            </div>
          </div>
        </div>
      </div>
    );
  }
}
